import Phaser from 'phaser'
import { TacadaLimpaHitzAdapter, startErrorText } from '../../../hitz/TacadaLimpaHitzAdapter'
import { button } from '../ui/button'
import { THEME, hex } from '../../../kit/theme'
import { sfx } from '../../../kit/sfx'
import { BALL_R, CUP_R, STEP_MS, SUB, gateAt, type Course, type Pt } from '../../../core/physics'
import { HOLES_PER_ROUND, roundPar } from '../../../core/rules'
import type { PuttRecord } from '../../../core/engine'

/** Table origin on the 720x1280 stage; the table is 640 x 1000 table px (1 table px = 1 stage px). */
const OX = 40, OY = 190
const MAX_DRAG = 320
const sx = (x: number) => OX + x / SUB
const sy = (y: number) => OY + y / SUB
const txt = (size: number, color: string = THEME.colors.text) => ({ fontFamily: THEME.font.family, fontSize: `${size}px`, color, fontStyle: 'bold' })

/**
 * Phaser view of Tacada Limpa: draws the hole the session says, turns a drag into a putt command (angle + strength) and plays
 * the putt's path back on the match clock. Rules live in src/core, the Hitz attempt in src/hitz/TacadaLimpaHitzAdapter.ts.
 */
export class GameScene extends Phaser.Scene {
  private levelId = '1'
  private round: TacadaLimpaHitzAdapter | null = null
  private table!: Phaser.GameObjects.Graphics
  private gates!: Phaser.GameObjects.Graphics
  private aim!: Phaser.GameObjects.Graphics
  private putter!: Phaser.GameObjects.Container
  private ring!: Phaser.GameObjects.Arc
  private hint!: Phaser.GameObjects.Text
  private ball!: Phaser.GameObjects.Arc
  private hud!: Phaser.GameObjects.Text
  private sub!: Phaser.GameObjects.Text
  private card: Phaser.GameObjects.Container | null = null
  private viewHole = -1
  private anim: PuttRecord | null = null
  private dragging = false
  private resultShown = false

  constructor() { super('Game') }

  init(data: { levelId?: string }) {
    this.levelId = data.levelId ?? '1'
    this.round = null
    this.viewHole = -1
    this.anim = null
    this.card = null
    this.dragging = false
    this.resultShown = false
  }

  create() {
    const { width, height } = this.scale
    this.cameras.main.setBackgroundColor(THEME.colors.bg)
    this.table = this.add.graphics()
    this.gates = this.add.graphics()
    this.aim = this.add.graphics()
    this.ring = this.add.circle(-100, -100, BALL_R / SUB + 14).setStrokeStyle(4, hex('panel')).setVisible(false)
    this.ball = this.add.circle(-100, -100, BALL_R / SUB, hex('ball')).setStrokeStyle(3, hex('cup'), 0.35).setVisible(false)
    this.buildPutter()
    this.hint = this.add.text(OX + 320, OY + 1000 - 40, 'Puxe o taco para trás e solte', txt(32, THEME.colors.panel))
      .setOrigin(0.5).setStroke(THEME.colors.feltDark, 8).setVisible(false)
    this.hud = this.add.text(OX, 40, '', txt(40))
    this.sub = this.add.text(OX, 100, '', txt(28, THEME.colors.textDim))
    this.events.on('smoke-move', () => { const rec = this.canPutt() ? this.round?.puttTowardCup() : null; if (rec) { this.swing(rec); this.startAnim() } })
    this.events.once('shutdown', () => this.events.off('smoke-move'))

    const wait = this.add.text(width / 2, height * 0.45, 'Aguardando a plataforma…', txt(36, THEME.colors.textDim)).setOrigin(0.5)
    TacadaLimpaHitzAdapter.open(this.levelId).then(
      (round) => { wait.destroy(); this.round = round; this.drawHole() },
      (err: unknown) => {
        wait.setText(startErrorText(err))
        button(this, width / 2, height * 0.6, 'MENU', () => this.scene.start('Menu'))
      }
    )

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => { if (this.canPutt()) { this.dragging = true; this.drawAim(p) } })
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => { if (this.dragging) this.drawAim(p) })
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (!this.dragging) return
      this.dragging = false
      this.aim.clear()
      const cmd = this.command(p)
      if (!cmd || !this.round) { this.putter.setVisible(false); return }
      const rec = this.round.putt(cmd.angle, cmd.strength)
      if (rec) { this.swing(rec); this.startAnim() }
    })
  }

  /** Where a real tap should start a drag (smoke hook): the ball. */
  target() { const b = this.round?.session.ball; return b ? { x: sx(b.x), y: sy(b.y) } : null }

  private canPutt() { return !!this.round && !this.round.result && this.round.session.ready && !this.anim && !this.card }

  /** Drag from the ball: the putt goes the opposite way, strength grows with the drag length. */
  private command(p: Phaser.Input.Pointer): { angle: number; strength: number } | null {
    const b = this.round!.session.ball
    const dx = sx(b.x) - p.x, dy = sy(b.y) - p.y
    const len = Math.hypot(dx, dy)
    if (len < 12) return null
    const strength = Math.round(Math.min(1, len / MAX_DRAG) * 1000)
    const angle = Math.round((Math.atan2(dy, dx) / (2 * Math.PI)) * 1000)
    return { angle: ((angle % 1000) + 1000) % 1000, strength }
  }

  /** A putter drawn once: in its local frame +x points away from the shot (back from the ball), the head sits at x = 0. */
  private buildPutter() {
    const shaft = this.add.rectangle(0, 0, 250, 9, hex('wood')).setOrigin(0, 0.5).setStrokeStyle(2, hex('woodDark'))
    const grip = this.add.rectangle(185, 0, 75, 15, hex('cup')).setOrigin(0, 0.5)
    const neck = this.add.rectangle(0, 0, 18, 12, hex('stone')).setOrigin(0, 0.5)
    const head = this.add.rectangle(0, 0, 22, 70, hex('stone')).setStrokeStyle(3, hex('cup'), 0.6)
    const face = this.add.rectangle(-9, 0, 4, 60, hex('panel'), 0.9)
    this.putter = this.add.container(-200, -200, [shaft, grip, neck, head, face]).setVisible(false)
  }

  /** Puts the putter behind the ball for a shot at `shotAngle` (radians), pulled back by `pull` px. */
  private placePutter(shotAngle: number, pull: number, alpha = 1, at: Pt = this.round!.session.ball) {
    const bx = sx(at.x), by = sy(at.y)
    const back = shotAngle + Math.PI, gap = BALL_R / SUB + 13 + pull
    this.putter.setPosition(bx + Math.cos(back) * gap, by + Math.sin(back) * gap).setRotation(back).setAlpha(alpha).setVisible(true)
  }

  private drawAim(p: Phaser.Input.Pointer) {
    const cmd = this.command(p)
    const b = this.round!.session.ball, bx = sx(b.x), by = sy(b.y)
    this.aim.clear()
    this.hint.setVisible(false)
    this.ring.setVisible(false)
    if (!cmd) { this.sub.setText('Puxe mais para trás para dar força'); return }
    const a = (cmd.angle / 1000) * 2 * Math.PI, k = cmd.strength / 1000
    this.placePutter(a, k * 110)
    // where the ball starts going: a dotted line of FIXED length, direction only. Strength shows only in the bar (and the
    // backswing), so the line cannot be used as a ruler to the cup; no bounce preview either: reading the walls is the game.
    // The same dots are the strength gauge: they light up from the ball outwards as the player pulls (the eye is on the putter).
    const dots = 8, lit = Math.max(1, Math.ceil(k * dots)), hot = k > 0.85
    for (let i = 1; i <= dots; i++) {
      const d = BALL_R / SUB + 10 + i * 26, on = i <= lit
      this.aim.fillStyle(hex(on ? (hot ? 'negative' : 'accent') : 'panel'), on ? 1 : 0.35)
        .fillCircle(bx + Math.cos(a) * d, by + Math.sin(a) * d, on ? 7 : 5)
    }
    const tip = BALL_R / SUB + 10 + (dots + 1) * 26
    const tx = bx + Math.cos(a) * tip, ty = by + Math.sin(a) * tip, side = a + Math.PI / 2
    this.aim.fillStyle(hex('panel'), 0.5).fillTriangle(
      tx + Math.cos(a) * 16, ty + Math.sin(a) * 16,
      tx + Math.cos(side) * 11, ty + Math.sin(side) * 11,
      tx - Math.cos(side) * 11, ty - Math.sin(side) * 11)
    // strength bar under the HUD
    const W = 300, X = OX + 640 - W, Y = 112
    this.aim.fillStyle(hex('bgDeep')).fillRoundedRect(X, Y, W, 22, 11)
    this.aim.fillStyle(hex(k > 0.85 ? 'negative' : 'accent')).fillRoundedRect(X, Y, Math.max(22, W * k), 22, 11)
    this.sub.setText(`Força ${Math.round(k * 100)}%`)
  }

  /**
   * The swing on release, at the spot the ball was struck from (`from`: the session already moved the ball to where it will
   * stop). The putter comes forward to the ball, stays a moment where it hit (follow-through) and fades there.
   */
  private swing(rec: PuttRecord) {
    this.tweens.killTweensOf(this.putter)
    const a = (rec.angle / 1000) * 2 * Math.PI, back = a + Math.PI
    this.placePutter(a, (rec.strength / 1000) * 110, 1, rec.from)
    const gap = BALL_R / SUB + 4
    this.tweens.add({
      targets: this.putter, x: sx(rec.from.x) + Math.cos(back) * gap, y: sy(rec.from.y) + Math.sin(back) * gap, duration: 70, ease: 'Quad.easeIn',
      onComplete: () => this.tweens.add({ targets: this.putter, alpha: 0, delay: 180, duration: 220, onComplete: () => this.putter.setVisible(false) })
    })
  }

  /** When it is the player's turn and nobody is dragging: the putter rests behind the ball, aimed at the cup, with a hint. */
  private showRest(t: number) {
    const r = this.round!, b = r.session.ball, cup = r.session.hole.course.cup
    const a = Math.atan2(cup.y - b.y, cup.x - b.x)
    if (!this.tweens.isTweening(this.putter)) this.placePutter(a, 6, 0.55)
    this.ring.setPosition(sx(b.x), sy(b.y)).setVisible(true).setScale(1 + 0.25 * Math.abs(Math.sin(t / 380))).setAlpha(0.7 - 0.4 * Math.abs(Math.sin(t / 380)))
    this.hint.setVisible(r.session.history.length < 2)
  }

  private startAnim() {
    const r = this.round
    if (!r) return
    this.anim = r.session.history[r.session.history.length - 1]
    sfx.play('tap')
  }

  private drawHole() {
    const r = this.round!
    const h = r.session.round[r.session.state.hole]
    this.viewHole = r.session.state.hole
    const c = h.course, g = this.table
    g.clear()
    // table: frame, felt
    g.fillStyle(hex('woodDark')).fillRoundedRect(OX - 14, OY - 14, 640 + 28, 1000 + 28, 18)
    g.fillStyle(hex('felt')).fillRect(OX, OY, 640, 1000)
    for (const z of c.zones) {
      if (z.kind === 'sand') { g.fillStyle(hex('sand')).fillRoundedRect(sx(z.x), sy(z.y), z.w / SUB, z.h / SUB, 24) }
      else {
        g.fillStyle(hex('ramp')).fillRect(sx(z.x), sy(z.y), z.w / SUB, z.h / SUB)
        g.lineStyle(4, hex('feltDark'), 0.8)
        const dir = Math.sign(z.dx ?? 0) || 1
        for (let ax = sx(z.x) + 60; ax < sx(z.x + z.w) - 40; ax += 120) for (let ay = sy(z.y) + 50; ay < sy(z.y + z.h); ay += 90) {
          g.lineBetween(ax - 20 * dir, ay, ax + 20 * dir, ay); g.lineBetween(ax + 20 * dir, ay, ax + 8 * dir, ay - 10); g.lineBetween(ax + 20 * dir, ay, ax + 8 * dir, ay + 10)
        }
      }
    }
    for (const w of c.walls) {
      g.fillStyle(hex('woodDark')).fillRect(sx(w.x), sy(w.y) + 6, w.w / SUB, w.h / SUB)
      g.fillStyle(hex('wood')).fillRect(sx(w.x), sy(w.y), w.w / SUB, w.h / SUB - 2)
    }
    g.lineStyle(14, hex('wood'))
    for (const d of c.diags) {
      const [p, q] = d.kind === '/'
        ? [{ x: (d.c + d.lo) / 2, y: (d.c - d.lo) / 2 }, { x: (d.c + d.hi) / 2, y: (d.c - d.hi) / 2 }]
        : [{ x: (d.lo + d.c) / 2, y: (d.lo - d.c) / 2 }, { x: (d.hi + d.c) / 2, y: (d.hi - d.c) / 2 }]
      g.lineBetween(sx(p.x), sy(p.y), sx(q.x), sy(q.y))
    }
    // cup and flag
    g.fillStyle(hex('cup')).fillCircle(sx(c.cup.x), sy(c.cup.y), CUP_R / SUB)
    g.lineStyle(4, hex('text')).lineBetween(sx(c.cup.x), sy(c.cup.y), sx(c.cup.x), sy(c.cup.y) - 70)
    g.fillStyle(hex('accent')).fillTriangle(sx(c.cup.x), sy(c.cup.y) - 70, sx(c.cup.x) + 40, sy(c.cup.y) - 58, sx(c.cup.x), sy(c.cup.y) - 46)
    this.placeBall(r.session.ball)
    this.ball.setVisible(true)
    this.updateHud()
  }

  private placeBall(p: Pt) { this.ball.setPosition(sx(p.x), sy(p.y)) }

  private drawGates(c: Course, t: number) {
    this.gates.clear()
    for (const gate of c.gates) {
      const r = gateAt(gate, t, c.gatePhaseMs)
      this.gates.fillStyle(hex('stone')).fillRoundedRect(sx(r.x), sy(r.y), r.w / SUB, r.h / SUB, 10)
    }
  }

  private updateHud() {
    const r = this.round!, s = r.session, h = s.round[this.viewHole]
    this.hud.setText(`Buraco ${this.viewHole + 1}/${HOLES_PER_ROUND} · ${h.def.name} · Par ${h.def.par}`)
    const strokes = this.viewHole === s.state.hole ? s.state.strokes : s.state.cards[this.viewHole]?.strokes ?? 0
    this.sub.setText(`Tacadas ${strokes} · Pontos ${s.state.score} · Volta: par ${roundPar(s.round)}`)
  }

  private showCard() {
    const r = this.round!, card = r.session.state.cards[this.viewHole]
    const { width } = this.scale
    const diff = card.strokes - card.par
    const label = !card.holed ? 'Não terminou' : card.strokes === 1 ? 'Na primeira!' : diff < 0 ? `${-diff} abaixo do par` : diff === 0 ? 'No par' : `${diff} acima do par`
    const bg = this.add.rectangle(0, 0, 560, 300, hex('panel')).setStrokeStyle(4, hex('stone'))
    const t1 = this.add.text(0, -90, label, txt(52, card.holed && diff <= 0 ? THEME.colors.positive : THEME.colors.text)).setOrigin(0.5)
    const t2 = this.add.text(0, -10, `${card.strokes} tacada${card.strokes > 1 ? 's' : ''} · par ${card.par}`, txt(34, THEME.colors.textDim)).setOrigin(0.5)
    const t3 = this.add.text(0, 70, `+${card.points} pts`, txt(56, THEME.colors.accent)).setOrigin(0.5)
    this.card = this.add.container(width / 2, OY + 420, [bg, t1, t2, t3])
    sfx.play(card.holed && diff <= 0 ? 'good' : 'bad')
    this.time.delayedCall(1500, () => {
      this.card?.destroy(); this.card = null
      if (!this.round?.result) this.drawHole()
    })
  }

  update(t: number, dt: number) {
    const r = this.round
    if (!r) return
    r.step(dt)
    const s = r.session
    const h = s.round[Math.max(0, this.viewHole)]
    if (h) this.drawGates(h.course, s.elapsedMs)
    if (this.anim) {
      const path = this.anim.result.path ?? [this.anim.result.stop]
      const i = Math.floor((s.elapsedMs - this.anim.atMs) / STEP_MS)
      this.placeBall(path[Math.min(Math.max(0, i), path.length - 1)])
      if (i >= path.length - 1) {
        const done = this.anim
        this.anim = null
        if (done.result.holed) this.ball.setVisible(false)
        if (s.state.hole !== this.viewHole || s.over) this.showCard()
        else this.updateHud()
      }
    } else if (!this.card && !this.dragging) this.updateHud()
    if (this.canPutt() && !this.dragging) this.showRest(t)
    else if (!this.dragging) { this.ring.setVisible(false); this.hint.setVisible(false); if (!this.tweens.isTweening(this.putter)) this.putter.setVisible(false) }
    if (r.result && !this.anim && !this.card && !this.resultShown) {
      this.resultShown = true
      this.time.delayedCall(300, () => this.scene.start('Result', r.result ?? undefined))
    }
  }
}
